import { useState } from "react";
import toast from "react-hot-toast";
import { useGlobalContext } from "../GlobalContext";

const useSettlement = () => {
  const { api, AdminId } = useGlobalContext();

  const [settlementLoading, setSettlementLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  // ===== SETTLEMENT REPORT =====
  const fetchSettlementReport = async (
    driverId,
    fromDate,
    toDate,
    setSettlementRows
  ) => {
    if (!driverId) {
      setSettlementRows([]);
      return [];
    }

    setSettlementLoading(true);

    try {
      const payload = {
        adminId: AdminId,
        driverCode: driverId,
        fromDate,
        toDate,
      };

      const res = await api.post(
        "/api/Transactions/v1.0/getSettlementReport",
        payload
      );

      if (res.data?.responseCode === "00") {
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        setSettlementRows(rows);
        return rows;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to fetch settlement report"
        );
      }

      setSettlementRows([]);
      return [];
    } catch (err) {
      console.error("Settlement API Error:", err);
      toast.error("Error fetching settlement report");
      setSettlementRows([]);
      return [];
    } finally {
      setSettlementLoading(false);
    }
  };

  // ===== SETTLE AMOUNT =====
  const handleSettleAmount = async (
    driverId,
    fromDate,
    toDate,
    payAmount,
    bonusAmount,
    setAmountPaid,
    refreshAnalytics,
    refreshSettlement
  ) => {
    if (!payAmount && !bonusAmount) {
      toast.error("Please enter amount or bonus");
      return false;
    }

    const loadingToastId = toast.loading("Settling amount...");

    try {
      setPaying(true);

      const payload = {
        adminId: AdminId,
        driverCode: driverId,
        fromDate,
        toDate,
        amount: Number(payAmount || 0),
        bonusAmount: Number(bonusAmount || 0),
      };

      const res = await api.post(
        "/api/Transactions/v1.0/settleDriverAmount",
        payload
      );

      if (res.data?.responseCode === "00") {
        const paid = Number(res.data?.data?.amountPaid || 0);
        setAmountPaid(paid);

        if (typeof refreshSettlement === "function") {
          await refreshSettlement();
        }

        if (typeof refreshAnalytics === "function") {
          await refreshAnalytics();
        }

        toast.success(
          res.data?.responseMessage || "Amount settled successfully",
          {
            id: loadingToastId,
          }
        );

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to settle amount", {
          id: loadingToastId,
        });
      } else {
        toast.dismiss(loadingToastId);
      }

      return false;
    } catch (err) {
      console.error("Settle Amount Error", err);

      toast.error("Something went wrong while settling amount", {
        id: loadingToastId,
      });

      return false;
    } finally {
      setPaying(false);
    }
  };

  return {
    fetchSettlementReport,
    handleSettleAmount,
    settlementLoading,
    paying,
  };
};

export default useSettlement;